import { Platform } from 'react-native'
import { tv } from 'tailwind-variants'

import { body2, caption, overline } from '@ui-kit/core/typography'

export const INPUT_HEIGHT = 48
export const MULTILINE_INPUT_HEIGHT = 80

export const getTextFieldStyles = ({
  borderColor,
  bgColor,
}: {
  borderColor: string
  bgColor: string
}) =>
  tv({
    slots: {
      container: '',
      fieldContainer: `${borderColor} relative flex`,
      childrenContainer: 'flex min-h-5 flex-col justify-center',
      label: [
        body2({ emphasis: 'medium' }),
        'absolute',
        Platform.select({
          web: 'pointer-events-none web:transition-all web:duration-200 web:ease-in-out',
          default: '',
        }),
      ],
      assistiveText: [caption(), 'pt-1'],
    },
    variants: {
      disabled: {
        true: {
          container: 'opacity-40',
        },
      },
      autoGrow: {
        true: {
          childrenContainer: 'flex-auto',
          fieldContainer: 'min-h-[48px]',
        },
        false: {
          childrenContainer: 'flex-1',
          fieldContainer: 'h-[48px]',
        },
      },
      status: {
        error: {
          assistiveText: 'text-error',
        },
        warning: {
          assistiveText: 'text-warning',
        },
      },

      isFocused: {
        true: {
          label: [overline({ emphasis: 'medium' })],
        },
      },
      isFilled: {
        true: {
          label: [overline({ emphasis: 'medium' })],
        },
      },
      hasLabel: {
        true: {},
      },
      multiline: {
        true: {
          fieldContainer: 'h-28',
          childrenContainer: 'justify-start',
        },
      },
      styleType: {
        outlined: {
          fieldContainer: `${bgColor} rounded-lg border px-2.5`,
          label: 'ios:left-2.5 android:left-2.5 top-1',
        },
        underlined: {
          fieldContainer: 'border-b px-0',
          label: 'top-0',
        },
      },
    },
    compoundVariants: [
      {
        status: 'error',
        className: {
          label: 'text-error',
          fieldContainer: 'border-error',
        },
      },
      {
        status: 'warning',
        className: {
          label: 'text-warning',
          fieldContainer: 'border-warning',
        },
      },
      {
        status: undefined,
        isFocused: true,
        className: {
          label: 'text-black',
          fieldContainer: 'border-black',
        },
      },
      {
        hasLabel: true,
        multiline: false,
        className: {
          childrenContainer: ['pt-5', 'android:pt-3 ios:pt-3'],
        },
      },
      {
        hasLabel: true,
        multiline: true,
        className: {
          childrenContainer: 'pt-5',
        },
      },
      {
        hasLabel: true,
        autoGrow: true,
        className: {
          childrenContainer: 'pt-5',
        },
      },
      {
        isFocused: false,
        isFilled: false,
        className: { label: ['top-1/2 -translate-y-1/2'] },
      },
      {
        isFocused: true,
        styleType: 'outlined',
        className: { label: `${bgColor} w-fit text-black/70 opacity-100` },
      },
      {
        isFilled: true,
        styleType: 'outlined',
        className: { label: `${bgColor} w-fit text-black/70 opacity-100` },
      },
    ],
    defaultVariants: {
      styleType: 'outlined',
      autoGrow: false,
      multiline: false,
    },
  })

export const textInput = tv({
  extend: body2,
  base: 'w-full border-0 p-0 leading-4 shadow-none outline-none',
  variants: {
    autoGrow: {
      true: 'pt-2',
    },
  },
})
